import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  BadgeWarningSubtle,
  Icon,
  IconSVG,
  SVGColorKey,
  Tooltip,
} from "@studio3t/generic-components";
import {
  Background,
  ControlButton,
  Controls,
  MiniMap,
  type Node,
  type NodeProps,
  type NodeTypes,
  Panel,
  ReactFlow,
  useReactFlow,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useReducedMotion } from "motion/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { useSchemasByService } from "@/hooks/useApi";
import type { Connector } from "@/types/api";
import { formatRate } from "@/utils/format";
import {
  connectorTone,
  dashPeriodSeconds,
  describeHop,
  type FlowState,
  type Hop,
  hopPace,
} from "./flowMotion";
import {
  ARROW_BOX,
  type ArrowNodeData,
  buildPipelineGraph,
  type PipelineFlowNode,
  type StageNodeData,
  type StageType,
} from "./pipelineGraph";
import { StageCardBadges } from "./StageCardBadges";
import {
  Card,
  FlowCanvas,
  Section,
  StageBody,
  StageCardBox,
  StageCardLink,
  StageHeader,
  StageHeaderLeft,
  StageLabelText,
  StageRow,
  StageRowText,
} from "./styles";
import { useDashMotion } from "./useDashMotion";

type StageNodeType = Node<StageNodeData, "stage">;
type ArrowNodeType = Node<ArrowNodeData, "arrow">;

export const HEADER_ICON: Record<StageType, Icon> = {
  source: Icon.Input,
  transform: Icon.Transform,
  sink: Icon.Output,
};
// Row 1 = service name, row 2 = resource. Transform stages use file/code icons.
export const SERVICE_ICON: Record<StageType, Icon> = {
  source: Icon.Database,
  transform: Icon.Folder,
  sink: Icon.Database,
};
export const RESOURCE_ICON: Record<StageType, Icon> = {
  source: Icon.Collection,
  transform: Icon.Code,
  sink: Icon.Collection,
};
const ARROW_ICON: Record<ArrowNodeData["direction"], Icon> = {
  right: Icon.Right,
  bottom: Icon.Bottom,
};

// The encoding badge (Figma badge/secondary/subtle). Pin the text colour to the
// badge token so it doesn't inherit the card link's green.
export const EncodingBadge = styled(BadgeSecondarySubtle)`
  color: ${(p) => p.theme.badgeSecondarySubtleText};
`;

// The "⚠ Unresolved" chip on an unresolved stage card, inline at the end of
// the resource row — same treatment as /newStudio's graph cards (the card
// has no room for a third row).
const NodeUnresolvedBadge = styled(BadgeWarningSubtle)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  color: ${(p) => p.theme.badgeWarningSubtleColor};
`;

function StageNode({ data }: NodeProps<StageNodeType>) {
  const { type } = data;
  const { pathname, search } = useLocation();
  // We're on /pipelines/:name — surface that name so the service page's back
  // button reads "Back to '<name>' Pipeline" and returns here.
  const segments = pathname.split("/").filter(Boolean);
  const pipelineName = decodeURIComponent(segments[segments.length - 1] ?? "");
  const backLabel = pipelineName ? `'${pipelineName}' Pipeline` : "Pipeline";
  const body = (
    <>
      <StageHeader $type={type}>
        <StageHeaderLeft>
          <IconSVG
            icon={HEADER_ICON[type]}
            size={16}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageLabelText>{data.label}</StageLabelText>
        </StageHeaderLeft>
        {data.encoding && <EncodingBadge>{data.encoding}</EncodingBadge>}
      </StageHeader>
      <StageBody>
        <StageRow>
          <IconSVG
            icon={SERVICE_ICON[type]}
            size={14}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageRowText>{data.serviceName}</StageRowText>
        </StageRow>
        <StageRow>
          <IconSVG
            icon={RESOURCE_ICON[type]}
            size={14}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageRowText $regular>{data.resource}</StageRowText>
          {data.unresolved && (
            <NodeUnresolvedBadge>
              <IconSVG
                icon={Icon.Warning}
                size={11}
                svgColorKey={SVGColorKey.warningIcon}
              />
              Unresolved
            </NodeUnresolvedBadge>
          )}
        </StageRow>
        {/* The schema the stage refs and the operations it takes, on one
            row under the resource. */}
        <StageCardBadges data={data} />
      </StageBody>
    </>
  );
  // An unresolved stage names a service that doesn't exist: no page to
  // open, so the card stays put.
  if (data.unresolved) {
    return (
      <StageCardBox
        $type={type}
        $unresolved
        $backed={data.backed}
        title="Service unresolved — it doesn't exist, so there is no page to open"
      >
        {body}
      </StageCardBox>
    );
  }
  return (
    <StageCardLink
      $type={type}
      $backed={data.backed}
      to={`/services/${encodeURIComponent(data.serviceName)}`}
      // Tell the service page where we came from, so its back button returns
      // to this pipeline instead of the Services list.
      state={{ from: { pathname: pathname + search, label: backLabel } }}
    >
      {body}
    </StageCardLink>
  );
}

/** What the connectors between stages know of the running pipeline: its
 *  pace and the state of the source's queue. Undefined is a canvas that
 *  knows nothing of the sort (the Studio graph), which keeps the arrow
 *  glyphs. */
const FlowContext = createContext<FlowState | undefined>(undefined);

// Figma 15891-149421/149422: a 2px line dashed 4 on, 4 off. The dashes
// travel one dash-plus-gap per cycle, so the motion loops seamlessly
// whatever the period.
const DASH = 4;
const DASH_GAP = 4;
const DASH_CYCLE = DASH + DASH_GAP;
const LINE_WIDTH = 2;
// Figma 15891-149423: an 8px filled dot with a 1px body-coloured edge on
// the stage the connector leaves.
const DOT_R = 3.5;
const DOT_STROKE = 1;
// Figma 15891-149426, and 16196-146362 for the queued red: a 6 by 10
// arrowhead on the stage the connector enters, filled in the connector's
// colour with the dot's edge, centred on the card edge as the dot is on
// the edge it leaves. The path is the mock's own, drawn pointing right
// with its centre at ARROW_CENTRE; the vertical connector turns with the
// drawing.
const ARROW_HEAD =
  "M0.788086 0.546887C0.963723 0.464746 1.17134 0.492083 1.32031 0.616223L7.32031 5.61622C7.43413 5.71122 7.5 5.85175 7.5 6.00001C7.5 6.14828 7.43413 6.28881 7.32031 6.3838L1.32031 11.3838C1.17134 11.5079 0.963723 11.5353 0.788086 11.4531C0.612394 11.3708 0.5 11.194 0.5 11V1.00001C0.5 0.806003 0.612394 0.629177 0.788086 0.546887Z";
const ARROW_CENTRE = { x: 4, y: 6 };

// The Metrics card's stream-buffer palette, so a red connector and a red
// bar segment say the same thing: queued red, in-flight green.
const STATUS = designVariables.theme.light.status;

// Only the dashed line moves; the dots stay put. The offset is driven a
// frame at a time by useDashMotion, not by a CSS animation, so a change
// of pace eases in rather than throwing the phase (see the hook).
const FlowLine = styled.line`
  stroke-dasharray: ${DASH} ${DASH_GAP};
`;

// The box is the gap itself; the dot at its start straddles the card
// edge, so the drawing is allowed to spill over.
const ArrowBox = styled.div<{ $direction: ArrowNodeData["direction"] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  width: ${(p) => (p.$direction === "right" ? ARROW_BOX.span : ARROW_BOX.across)}px;
  height: ${(p) => (p.$direction === "right" ? ARROW_BOX.across : ARROW_BOX.span)}px;
`;

/** A connector spanning the gap: a dashed line the whole gap long, from
 *  a filled dot on the edge of the stage it leaves to an arrowhead on the
 *  edge of the stage it enters (Figma 15891-149421..149426). Drawn
 *  left to right and rotated for a downward connector, so one drawing
 *  serves both. The dashes move at the pipeline's pace, or not at
 *  all when nothing flows or the viewer prefers reduced motion; the colour
 *  is green, red only while records queue. */
function FlowConnector({
  data,
  state,
}: {
  data: ArrowNodeData;
  state: FlowState;
}) {
  const theme = useTheme();
  const calm = useReducedMotion() === true;
  // Each hop moves at its own pace: the source's connector at the read
  // rate, the ones after the transform at the rate it passes records on.
  const hop: Hop = data.from === "source" ? "source" : "transform";
  const periodS = calm ? null : dashPeriodSeconds(hopPace(state, hop));
  const lineRef = useDashMotion(periodS, DASH_CYCLE);
  const tone = connectorTone(state, hop);
  // The tooltip names the stage the connector enters: a pipeline with no
  // transform reads straight from the source into the sink.
  const to = data.to === "sink" ? "sink" : "transform";
  const speed = describeHop(state, hop, formatRate, to);
  // Green unless records queue: a still connector keeps the colour and
  // only its dashes stop, so the line never looks disabled.
  const color = tone === "queued" ? STATUS.error : STATUS.confirm;
  const { span, across } = ARROW_BOX;
  const mid = across / 2;
  return (
    <Tooltip
      text={speed}
      side={data.direction === "right" ? "top" : "right"}
      sideOffset={8}
    >
      <ArrowBox $direction={data.direction} data-tone={tone} data-speed={speed}>
        <svg
          width={span}
          height={across}
          viewBox={`0 0 ${span} ${across}`}
          overflow="visible"
          style={
            data.direction === "bottom"
              ? { transform: "rotate(90deg)" }
              : undefined
          }
          // Named through aria, not a <title>: browsers render an SVG title
          // as a native tooltip, which would double the design-system one.
          role="img"
          aria-label={speed}
        >
          {/* The line runs edge to edge and is painted first, so the dot
            and the arrowhead cover its ends. */}
          <FlowLine
            ref={lineRef}
            x1={0}
            y1={mid}
            x2={span}
            y2={mid}
            stroke={color}
            strokeWidth={LINE_WIDTH}
          />
          <circle
            cx={0}
            cy={mid}
            r={DOT_R}
            fill={color}
            stroke={theme.semanticTextBody}
            strokeWidth={DOT_STROKE}
          />
          <path
            d={ARROW_HEAD}
            transform={`translate(${span - ARROW_CENTRE.x} ${mid - ARROW_CENTRE.y})`}
            fill={color}
            stroke={theme.semanticTextBody}
            strokeWidth={DOT_STROKE}
            strokeLinejoin="round"
          />
        </svg>
      </ArrowBox>
    </Tooltip>
  );
}

// The connector between two stages: on a canvas that knows the pipeline's
// flow, a dashed line whose dashes move at its pace and whose colour says
// whether records queue; elsewhere (the Studio graph) the →/↓ arrow glyph
// (Figma Right/Bottom), centred in the same box.
export function ArrowNode({ data }: NodeProps<ArrowNodeType>) {
  const theme = useTheme();
  const state = useContext(FlowContext);
  if (state !== undefined) {
    return <FlowConnector data={data} state={state} />;
  }
  return (
    <ArrowBox $direction={data.direction}>
      <IconSVG
        icon={ARROW_ICON[data.direction]}
        size={16}
        colors={{ default: theme.semanticTextLight }}
      />
    </ArrowBox>
  );
}

const defaultNodeTypes = { stage: StageNode, arrow: ArrowNode };

// Don't zoom past 100% — small graphs would otherwise blow the stage cards up
// to fill the canvas. Shared by the initial fit and the Controls fit button.
const fitViewOptions = { maxZoom: 1, padding: 0.2 };

// Modifier label matches the repo's other shortcut hints (command palette).
const IS_MAC = navigator.platform.toUpperCase().includes("MAC");
const ZOOM_HINT_TEXT = `Use ${IS_MAC ? "⌘" : "Ctrl"} + scroll to zoom`;
// The platform zoom modifier — same key React Flow's default
// zoomActivationKeyCode resolves to.
const ZOOM_KEY = IS_MAC ? "Meta" : "Control";
// The hover hint fades out on its own after this long.
const ZOOM_HINT_TIMEOUT_MS = 4000;

const TOOLTIP = designVariables.ui.tooltip;
const SINGLE_LINE = designVariables.type.singleLine;

// Google-Maps-style hint: a quiet tooltip-coloured pill at the top of the
// canvas explaining how to zoom while the canvas is locked. Purely
// informational — it never captures the pointer, and it only ever fades.
const ZoomHintPanel = styled(Panel)<{ $visible: boolean }>`
  padding: ${TOOLTIP.paddingh}px ${TOOLTIP.paddingw}px;
  border-radius: ${TOOLTIP.corner}px;
  background: ${(p) => p.theme.tooltipBackground};
  color: ${(p) => p.theme.tooltipText};
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  opacity: ${(p) => (p.$visible ? 0.9 : 0)};
  transition: opacity 240ms ease;
  pointer-events: none;
`;

// Neutral group container behind a stacked middleware / sink column.
const GROUP_BACKGROUND = designVariables.primitives.neutrals["20"];
const GROUP_CORNER = designVariables.primitives.corner.xl;

// Minimap node colours by stage type (rainbow accents); arrows stay invisible.
const MINIMAP_COLOR: Record<StageType, string> = {
  source: designVariables.primitives.rainbow.alpha.yellow,
  transform: designVariables.primitives.rainbow.alpha.orange,
  sink: designVariables.primitives.rainbow.alpha.turquoise,
};

function minimapNodeColor(node: Node): string {
  if (node.type === "group") return GROUP_BACKGROUND;
  if (node.type === "stage")
    return MINIMAP_COLOR[(node.data as StageNodeData).type];
  return "transparent";
}

// Zooms the viewport in on the focused node, centered — /newStudio's code
// cursor → graph card focus. `fittedRef` (parent-owned, so it survives
// canvas remounts) remembers the node already fitted: an edit that remounts
// the canvas with the same focus must not move the viewport again. The
// cursor leaving every stage (null) keeps the last viewport rather than
// jumping back out.
function FocusNode({
  id,
  fittedRef,
}: {
  id: string | null | undefined;
  fittedRef?: { current: string | null };
}) {
  const rf = useReactFlow();
  useEffect(() => {
    if (!id || !rf.getNode(id)) return;
    if (fittedRef?.current === id) return;
    if (fittedRef) fittedRef.current = id;
    // Next frame, so freshly (re)mounted nodes are measured before fitting.
    const frame = requestAnimationFrame(() => {
      rf.fitView({
        nodes: [{ id }],
        duration: 300,
        maxZoom: 1,
        padding: 0.2,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [id, rf, fittedRef]);
  return null;
}

// One tooltip-carrying +/−/fit button (design-system Tooltip on React
// Flow's ControlButton primitive).
function CanvasControlButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: Icon;
  onClick: () => void;
}) {
  return (
    <Tooltip text={label} side="right">
      <ControlButton aria-label={label} onClick={onClick}>
        <IconSVG
          icon={icon}
          size={12}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </ControlButton>
    </Tooltip>
  );
}

// The +/−/fit controls column. React Flow's built-in buttons can't carry
// the design-system tooltips, so they're re-implemented on the same
// ControlButton primitive with the same actions.
function CanvasControls({ extra }: { extra?: ReactNode }) {
  const rf = useReactFlow();
  return (
    <Controls showZoom={false} showFitView={false} showInteractive={false}>
      <CanvasControlButton
        label="Zoom in"
        icon={Icon.Plus}
        onClick={() => void rf.zoomIn()}
      />
      <CanvasControlButton
        label="Zoom out"
        icon={Icon.Minus}
        onClick={() => void rf.zoomOut()}
      />
      <CanvasControlButton
        label="Fit view"
        icon={Icon.Expand}
        onClick={() => void rf.fitView(fitViewOptions)}
      />
      {extra}
    </Controls>
  );
}

interface PipelineFlowCanvasProps {
  nodes: PipelineFlowNode[];
  /** Custom node renderers — defaults to this page's stage cards and arrow
   *  icons. */
  nodeTypes?: NodeTypes;
  /** Remounts React Flow when it changes — the canvas is uncontrolled, so
   *  new nodes only apply through a remount. */
  canvasKey?: string;
  /** Node to zoom in on, centered, whenever it changes. */
  focusNodeId?: string | null;
  /** Parent-owned memory of the node already fitted — lets a remounted
   *  canvas skip re-fitting the same focus (no motion on edits). */
  focusFittedRef?: { current: string | null };
  /** Start the (uncontrolled) viewport here instead of fitView — with
   *  onViewportChange, the parent carries the viewport across canvasKey
   *  remounts so content updates don't move the canvas. */
  initialViewport?: Viewport;
  onViewportChange?: (viewport: Viewport) => void;
  /** Extra buttons appended to the +/−/fit controls column. */
  controlsExtra?: ReactNode;
  /** The pipeline's pace and queue state, which drive the dashed
   *  connectors between stages. Left out, the connectors are plain arrow
   *  glyphs. */
  flow?: FlowState;
}

/** The read-only pan/zoom canvas around a built pipeline graph — React Flow
 *  wiring, group styling, minimap and the ⌘/Ctrl+scroll zoom UX. Shared by
 *  the pipeline detail page and /newStudio's Full pipeline graph view; the
 *  parent provides the sized container. */
export function PipelineFlowCanvas({
  nodes,
  nodeTypes = defaultNodeTypes,
  canvasKey,
  focusNodeId,
  focusFittedRef,
  initialViewport,
  onViewportChange,
  controlsExtra,
  flow,
}: PipelineFlowCanvasProps) {
  // Hover hint ("Use ⌘/Ctrl + scroll to zoom"): fades in on mouse-enter,
  // fades out after a few seconds or as soon as the user moves the viewport —
  // at that point they clearly know the gesture.
  const [hintVisible, setHintVisible] = useState(false);
  const hintTimer = useRef<number | undefined>(undefined);

  const hideHint = useCallback(() => {
    window.clearTimeout(hintTimer.current);
    setHintVisible(false);
  }, []);

  const showHint = useCallback(() => {
    setHintVisible(true);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(
      () => setHintVisible(false),
      ZOOM_HINT_TIMEOUT_MS,
    );
  }, []);

  // Don't leave the fade-out timer running past unmount.
  useEffect(() => () => window.clearTimeout(hintTimer.current), []);

  // React Flow's preventScrolling swallows every wheel event over the pane —
  // even ones its zoom filter then rejects — which would leave plain scroll
  // dead over the canvas. Track the zoom modifier ourselves and let React
  // Flow capture the wheel only while it's held; otherwise the page scrolls
  // past the canvas normally. Ctrl+scroll and pinch zoom stay live either
  // way: browsers mark those wheel events ctrlKey, which the handler always
  // accepts.
  const [zoomKeyHeld, setZoomKeyHeld] = useState(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === ZOOM_KEY) setZoomKeyHeld(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === ZOOM_KEY) setZoomKeyHeld(false);
    };
    // Releasing the key outside the window would otherwise leave it "stuck".
    const reset = () => setZoomKeyHeld(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", reset);
    };
  }, []);

  const rfNodes = nodes.map((n) =>
    n.type === "group"
      ? {
          ...n,
          // Behind the stage cards and arrows.
          zIndex: -1,
          style: {
            width: n.width,
            height: n.height,
            background: GROUP_BACKGROUND,
            border: "none",
            borderRadius: GROUP_CORNER,
          },
        }
      : n,
  );

  return (
    <CanvasWrap onMouseEnter={showHint} onMouseLeave={hideHint}>
      <FlowContext.Provider value={flow}>
        <ReactFlow
          // Uncontrolled: React Flow owns node state so it keeps the measured
          // dimensions across parent re-renders (the detail page polls
          // metrics). `key` remounts it when the graph changes.
          key={canvasKey}
          defaultNodes={rfNodes}
          nodeTypes={nodeTypes}
          // A carried-over viewport wins over the initial fit — remounts then
          // start exactly where the previous canvas left off.
          fitView={!initialViewport}
          defaultViewport={initialViewport}
          fitViewOptions={fitViewOptions}
          // The default minZoom (0.5) clamps fitView on large graphs — they'd
          // overflow to the canvas edges instead of shrinking into the 20%
          // fit padding. Allow zooming out far enough to always fit.
          minZoom={0.1}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          // Keyboard users reach a stage through the link card inside its
          // node; the node wrapper itself and the arrow nodes carry no action,
          // so they are not tab stops (TTTL-186).
          nodesFocusable={false}
          edgesFocusable={false}
          // Plain scroll passes through to the page, so reading past the
          // graph never hijacks into zooming. Zooming works through
          // deliberate gestures only: ⌘/Ctrl + scroll (React Flow's
          // platform-aware zoomActivationKeyCode), pinch — which is also
          // what browsers report Ctrl+scroll as, and React Flow rejects
          // Ctrl+wheel outright when zoomOnPinch is off — and the +/− and
          // fit-view buttons. Dragging pans as usual.
          zoomOnScroll={false}
          zoomOnPinch
          zoomOnDoubleClick={false}
          preventScrolling={zoomKeyHeld}
          // Any viewport move means the user has found a zoom gesture — the
          // hint has done its job. Also where the parent snapshots the
          // viewport for seamless remounts.
          onMove={(_, viewport) => {
            hideHint();
            onViewportChange?.(viewport);
          }}
          proOptions={{ hideAttribution: true }}
        >
          <FocusNode id={focusNodeId} fittedRef={focusFittedRef} />
          <Background gap={16} />
          <CanvasControls extra={controlsExtra} />
          <MiniMap pannable zoomable nodeColor={minimapNodeColor} />
          <ZoomHintPanel
            position="top-center"
            $visible={hintVisible}
            aria-hidden={!hintVisible}
          >
            {ZOOM_HINT_TEXT}
          </ZoomHintPanel>
        </ReactFlow>
      </FlowContext.Provider>
    </CanvasWrap>
  );
}

// Fills whatever sized container the parent provides. The node wrapper is
// non-interactive (read-only canvas); re-enable pointer events on the stage
// link so clicking a node still navigates to its service.
const CanvasWrap = styled.div`
  width: 100%;
  height: 100%;

  .react-flow__node a {
    pointer-events: auto;
  }

  /* The connectors carry a hover tooltip with their speed. React Flow
     writes pointer-events: none inline on a node that is neither
     draggable nor selectable, so this has to win over that. */
  .react-flow__node-arrow {
    pointer-events: auto !important;
  }
`;

interface PipelineFlowProps {
  pipeline: Partial<Connector> | null;
  unresolvedServices?: Set<string>;
  header?: ReactNode;
  /** The pipeline's pace and queue state, driving the connectors. */
  flow: FlowState;
}

export function PipelineFlow({
  pipeline,
  unresolvedServices,
  header,
  flow,
}: PipelineFlowProps) {
  // Schema badge names: documents may reference registry entries by id
  // (the Studio's rename-proof shape) — the live list of each service the
  // schemas block names turns those into display names (schemas live per
  // service, TTTL-319). Name-based refs pass through unchanged.
  const schemaServices = useMemo(
    () =>
      Array.from(
        new Set(
          (pipeline?.schemas ?? [])
            .map((s) => s.service_name)
            .filter((name): name is string => !!name),
        ),
      ),
    [pipeline?.schemas],
  );
  const { entries: schemaEntries } = useSchemasByService(schemaServices);

  if (!pipeline) return null;

  const { nodes: builtNodes } = buildPipelineGraph(
    pipeline,
    unresolvedServices,
  );
  // Entry ids are unique only within one service's store (TTTL-319), so
  // the lookup is keyed by the entry's service AND id; a stage whose block
  // entry names no service keeps the raw ref.
  const namesByServiceId = new Map<string, string>(
    schemaEntries.flatMap((s) =>
      s.name ? [[`${s.serviceName}:${s.id}`, s.name]] : [],
    ),
  );
  const nodes: PipelineFlowNode[] = builtNodes.map((n) =>
    n.type === "stage" && n.data.schemaName && n.data.schemaService
      ? {
          ...n,
          data: {
            ...n.data,
            schemaName:
              namesByServiceId.get(
                `${n.data.schemaService}:${n.data.schemaName}`,
              ) ?? n.data.schemaName,
          },
        }
      : n,
  );

  return (
    <Section>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {header}
        <FlowCanvas>
          <PipelineFlowCanvas
            nodes={nodes}
            canvasKey={pipeline.name ?? "pipeline"}
            flow={flow}
          />
        </FlowCanvas>
      </Card>
    </Section>
  );
}
